package nl.volleyplanner.nevoboservice.api

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.engine.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.cache.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.logging.*
import io.ktor.client.plugins.resources.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import io.ktor.util.reflect.*
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.json.Json
import kotlinx.serialization.modules.SerializersModule
import kotlinx.serialization.modules.contextual
import nl.volleyplanner.environment.RUNTIME_ENVIRONMENT
import nl.volleyplanner.ktor.MockConfig
import nl.volleyplanner.ktor.MockingHttpClientEngine
import nl.volleyplanner.ktor.RateLimiterPlugin
import nl.volleyplanner.ktor.UseMockStrategy
import okio.FileSystem
import okio.Path
import okio.Path.Companion.toPath
import kotlin.time.ExperimentalTime

expect val mockDirectoryPath: Path
expect val mockFileSystem: FileSystem
expect val apiHttpEngine: HttpClientEngine
expect val ktorLoggingEnabled: Boolean
expect val ktorLogger: Logger

interface NevoboAPIParameter

data class NevoboAPIValueParameter(
    val key: String,
    val value: String
) : NevoboAPIParameter

data class NevoboAPICollectionParameter(
    val key: String,
    val value: Collection<String>
) : NevoboAPIParameter


/**
 * Creates an API Client for the Nevobo API
 *
 * As the Nevobo API currently does not support CORS from unknown hosts. You can create a proxy
 * on your own host and base path routing requests to the actual API
 *
 * @see https://api.nevobo.nl/
 * @constructor Creates an instance for the given host and base path
 */
@OptIn(ExperimentalTime::class)
class NevoboAPIKtorClient constructor(
    val baseUrl: String = "https://api.nevobo.nl/",
    val mockConfig: MockConfig = MockConfig.NO_MOCK,
    loggingEnabled: Boolean = ktorLoggingEnabled
) {
    companion object {
        /**
         * Default instance that works out of the box
         */
        val default = NevoboAPIKtorClient("https://api.nevobo.nl/")

        /**
         * Creates a client that discovers the mock strategy if not provided
         *
         * If no mock config is provided. Tries to read the `nevoboapi.mock.dir` environment variable.
         * Mocking is disabled if no mock config is provided and no environment variable is set.
         */
        fun createNevoboClient(
            baseUrl: String = "https://api.nevobo.nl/",
            mockConfig: MockConfig? = null
        ) : NevoboAPIKtorClient {
            val mockConfigToUse = mockConfig
                ?: RUNTIME_ENVIRONMENT.getEnv("NEVOBO_API_MOCK_DIR")
                    ?.let { mockDirectory ->
                        MockConfig(
                            createMockResponses = true,
                            useMock = UseMockStrategy.MockAndDelegate,
                            mockDirectory = mockDirectory.toPath(true)
                        )
                    }
                ?: MockConfig.NO_MOCK

            return NevoboAPIKtorClient(baseUrl, mockConfigToUse)
        }
    }

    init {
        require(baseUrl.endsWith("/")) { "To properly handle API resources for all hosts the base URL must end with a slash (/)" }
    }

    private val nevoboApiSerializerModule = SerializersModule {
        contextual(NevoboDateTimeSerializer())
        include(identifiersModule)
    }

    private val mockApiEngine = MockingHttpClientEngine(
        apiHttpEngine, mockFileSystem, mockConfig, ktorLogger
    )

    private val apiEngine = if (mockConfig.mockEnabled) mockApiEngine else apiHttpEngine

    @OptIn(ExperimentalSerializationApi::class)
    private val HTTP_CLIENT = HttpClient(apiEngine) {
        expectSuccess = true

        // Install RetainDefaultRequestBaseUrl *before* defaultRequest as otherwise DefaultRequest
        // will see Nevobo resource URLs as a new base URL root
        install(RetainDefaultRequestBaseUrl)
        defaultRequest {
            url(baseUrl)
        }

        if (loggingEnabled) {
            install(Logging) {
                logger = ktorLogger
                level = if (loggingEnabled) LogLevel.INFO else LogLevel.NONE
            }
        }
        install(Resources) {}
        install(RateLimiterPlugin) {
            maxRequestsPerMinute = 100
            minDurationBetweenRequestsMillis = 250
        }
        install(ContentNegotiation) {
            json(Json {
                prettyPrint = true
                isLenient = true
                explicitNulls = false
                ignoreUnknownKeys = true
                serializersModule = nevoboApiSerializerModule
            }, contentType = ContentType("application", "ld+json"))
        }
        install(HttpCache) {
            isShared = true
        }
    }

    private suspend inline fun <reified T : NevoboAPIType> getPagedEntityForField(resourcePath: String, pageNumber: Int = 1, requestParameters: List<NevoboAPIParameter> = emptyList()) : PagedResponse<T> {
        return HTTP_CLIENT.get(resourcePath) {
            url {
                parameters.append("page", pageNumber.toString())
                requestParameters.forEach { param ->
                    when (param) {
                        is NevoboAPIValueParameter -> parameters.append(param.key, param.value)
                        is NevoboAPICollectionParameter -> param.value.forEach { parameters.append(param.key, it) }
                    }
                }
            }
        }.body(typeInfo<PagedResponse<T>>())
    }

    private suspend inline fun <reified T : NevoboAPIType> getEntityCount(resourcePath: String, parameters: List<NevoboAPIParameter> = emptyList()) : Int {
        val page = getPagedEntityForField<T>(resourcePath, 1, parameters)
        return page.totalItems
    }

    private suspend inline fun <reified T : NevoboAPIType> getAllEntities(resourcePath: String, parameters: List<NevoboAPIParameter> = emptyList()) : List<T> {
        val initialResult = getPagedEntityForField<T>(resourcePath, 1, parameters)

        val remainingRecords = coroutineScope {
            (2..initialResult.paginationData.lastPage)
                .map { pageNumber ->
                    async { getPagedEntityForField<T>(resourcePath, pageNumber, parameters) }
                }
                .awaitAll()
        }

        return initialResult.records + remainingRecords.flatMap { it.records }
    }

    private suspend inline fun <reified SINGLE_ENTITY_R : NevoboAPIType> getSingleEntity(identifier: NevoboAPIIdentifier<SINGLE_ENTITY_R>) : SINGLE_ENTITY_R? {
        val urlPath = identifier.getIdentifierURLPath()
        val response = HTTP_CLIENT.get(urlPath) {
            expectSuccess = false
        }
        return if (response.status != HttpStatusCode.NotFound) {
            response.body<SINGLE_ENTITY_R>(typeInfo<SINGLE_ENTITY_R>())
        } else {
            null
        }
    }

    suspend fun determineCurrentSeizoen(): Seizoen {
        val competities = getCompetities()
        val seizoenIdentifiers = competities.map { it.seizoen }.toSet()
        require(seizoenIdentifiers.size == 1) { "Found multiple seasons during sync: ${seizoenIdentifiers.map { it.naam }}" }
        return getSeizoen(seizoenIdentifiers.single())!!
    }

    suspend fun getSeizoen(seizoenIdentifier: SeizoenIdentifier) : Seizoen? = getSingleEntity(seizoenIdentifier)

    suspend fun getVereniging(verenigingIdentifier: VerenigingIdentifier) : Vereniging? = getSingleEntity(verenigingIdentifier)

    suspend fun getVerenigingen() : List<Vereniging> = getAllEntities("/relatiebeheer/verenigingen")

    suspend fun getCompetitie(competitieIdentifier: CompetitieIdentifier) : Competitie? = getSingleEntity(competitieIdentifier)

    suspend fun getCompetities(competitiesParameters: GetCompetitiesParameters = GetCompetitiesParameters()) : List<Competitie> {
        return getAllEntities("/competitie/competities", competitiesParameters.getParameters())
    }

    suspend fun getNiveau(niveauIdentifier: NiveauIdentifier) : Niveau? = getSingleEntity(niveauIdentifier)

    suspend fun getKlasse(klasseIdentifier: KlasseIdentifier) : Klasse? = getSingleEntity(klasseIdentifier)

    suspend fun getPoules(getPoulesParameters: GetPoulesParameters = GetPoulesParameters()) : List<Poule> {
        return getAllEntities("/competitie/poules", getPoulesParameters.getParameters())
    }

    suspend fun getPoule(pouleIdentifier: PouleIdentifier) : Poule? = getSingleEntity(pouleIdentifier)

    suspend fun getPouleIndeling(pouleIndelingIdentifier: PouleIndelingIdentifier) : PouleIndeling? = getSingleEntity(pouleIndelingIdentifier)

    suspend fun getPouleIndelingen(pouleIndelingenParameters: GetPouleIndelingenParameters) : List<PouleIndeling> {
        return getAllEntities("/competitie/pouleindelingen", pouleIndelingenParameters.getParameters())
    }

    suspend fun getPuntenTelMethode(puntenTelMethodeIdentifier: PuntenTelMethodeIdentifier) = getSingleEntity(puntenTelMethodeIdentifier)

    suspend fun getTeam(teamIdentifier: TeamIdentifier) : Team? = getSingleEntity(teamIdentifier)

    /**
     * Retrieves all teams from Nevobo API
     * Beware: there are 10k+ teams - this can take a while
     */
    suspend fun getTeams(teamsParameters: GetTeamsParameters) : List<Team> {
        return getAllEntities("/competitie/teams", teamsParameters.getParameters())
    }

    suspend fun getTeamsCount(teamsParameters: GetTeamsParameters) : Int {
        return getEntityCount<Team>("/competitie/teams", teamsParameters.getParameters())
    }

    suspend fun getSpeelzaal(speelzaalIdentifier: SpeelzaalIdentifier) : Speelzaal? = getSingleEntity(speelzaalIdentifier)

    suspend fun getSpeelzalen(speelzalenParameters: GetSpeelzalenParameters) : List<Speelzaal> {
        return getAllEntities("/accommodatie/speelzalen", speelzalenParameters.getParameters())
    }

    suspend fun getSpeelveld(speelveldIdentifier: SpeelveldIdentifier) : Speelveld? = getSingleEntity(speelveldIdentifier)

    suspend fun getSpeelvelden(speelveldenParameters: GetSpeelveldenParameters) : List<Speelveld> {
        return getAllEntities("/accommodatie/speelvelden", speelveldenParameters.getParameters())
    }

    suspend fun getWedstrijd(wedstrijdIdentifier: WedstrijdIdentifier) : Wedstrijd? = getSingleEntity(wedstrijdIdentifier)

    suspend fun getWedstrijd(wedstrijdUuid: String) : Wedstrijd? = getSingleEntity(WedstrijdUUIDIdentifier(wedstrijdUuid))

    suspend fun getAllWedstrijdenForPoule(pouleIdentifier: PouleIdentifier) : List<Wedstrijd> {
        return getAllEntities("/competitie/wedstrijden", listOf(NevoboAPIValueParameter("poule", pouleIdentifier.getIdentifierURLPath())))
    }

    suspend fun getAllWedstrijdenForVereniging(verenigingIdentifier: VerenigingIdentifier) : List<Wedstrijd> {
        return getAllEntities("/competitie/wedstrijden", listOf(NevoboAPIValueParameter("vereniging", verenigingIdentifier.getIdentifierURLPath())))
    }
}
